import { useState } from "react"
import { motion } from "framer-motion"
import { FaTimes, FaEye, FaEyeSlash } from "react-icons/fa"
import { useAuth } from "./AuthContext"
import { useNavigate } from "react-router-dom"

const AccountLinkingModal = ({ isOpen, onClose, email, authMethods = [] }) => {
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { linkWithGoogle } = useAuth()
  const navigate = useNavigate()

  const handleLinkAccounts = async (e) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    if (!password) {
      setError("Please enter your password")
      setIsLoading(false)
      return
    }

    try {
      const result = await linkWithGoogle(password)
      if (result.success) {
        // Get the user's role from the result
        const { role } = result

        // Set localStorage items
        localStorage.setItem("userId", result.user.uid)
        localStorage.setItem("role", role)
        localStorage.setItem("libraryId", result.libraryId || "")

        onClose()

        // Redirect based on role
        if (role === "admin") {
          navigate("/admin-dashboard")
        } else if (role === "owner") {
          navigate("/owner-dashboard")
        } else {
          navigate("/libraries")
        }
      }
    } catch (error) {
      console.error("Error linking accounts:", error)
      setError("Failed to link accounts. Please check your password and try again.")
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
          <FaTimes />
        </button>
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">Link Your Accounts</h2>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            An account already exists with the email <strong>{email}</strong> using{" "}
            {authMethods.includes("password") ? "email/password" : authMethods[0]} authentication.
          </p>
          <p className="text-gray-600">
            You can link your Google account with your existing account by entering your password below.
          </p>
        </div>

        <form onSubmit={handleLinkAccounts} className="space-y-4">
          <div className="relative">
            <label htmlFor="link-password" className="block text-sm font-medium text-gray-700 mb-1">
              Password for {email}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="link-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full text-black px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2 text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Linking...
                </span>
              ) : (
                "Link Accounts"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}

export default AccountLinkingModal
